/**
 * Lists Page Context
 *
 * Eliminates prop drilling through LeadListsView → LeadListDetailPanel.
 * Provides list-related actions and state that LeadListDetailPanel needs
 * without requiring LeadListsView to forward them.
 */

import { createContext, useContext } from 'react';
import type { Lead } from '@/types';

interface ListsPageContextValue {
  // Actions drilled through LeadListsView
  onAssignCampaign?: (listId: string, campaignId: string | null) => void;
  onCreateCampaignFromList?: (listId: string, listName: string) => void;
  onBulkEnrich?: (leadIds: string[]) => Promise<{ enriched: number; skipped: number }>;
  onMarkListEnriched?: (listId: string) => void;
  onMarkLeadEnriched?: (leadId: string, enriched: boolean) => void;
  onSyncLeadsToMonday?: (leadIds: string[], options?: { listType?: 'company' | 'contact'; listName?: string }) => Promise<{
    updated: number;
    created: number;
    failed: number;
    errors?: string[];
  }>;
  onImportLeads?: (
    leads: Omit<Lead, 'id' | 'apolloEnriched' | 'apolloData'>[],
    listId?: string,
    options?: { markAsEnriched?: boolean }
  ) => Promise<{ created: number; skipped: number }>;
  enrichingLeads: Set<string>;
}

const ListsPageContext = createContext<ListsPageContextValue | null>(null);

export function ListsPageProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: ListsPageContextValue;
}) {
  return (
    <ListsPageContext.Provider value={value}>
      {children}
    </ListsPageContext.Provider>
  );
}

export function useListsPage(): ListsPageContextValue {
  const ctx = useContext(ListsPageContext);
  if (!ctx) {
    throw new Error('useListsPage must be used within a ListsPageProvider');
  }
  return ctx;
}
